const DATABASE_NAME = 'newo-storage';
const STORE_NAME = 'storage';
const DATABASE_VERSION = 1;

export function getDb(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		if (
			typeof window === 'undefined' ||
			typeof window.indexedDB === 'undefined'
		) {
			reject(new Error('IndexedDB is not available.'));
			return;
		}

		const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

		request.onupgradeneeded = () => {
			const database = request.result;

			if (!database.objectStoreNames.contains(STORE_NAME)) {
				database.createObjectStore(STORE_NAME);
			}
		};

		request.onsuccess = () => {
			resolve(request.result);
		};

		request.onerror = () => {
			reject(request.error ?? new Error('Failed to open IndexedDB.'));
		};
	});
}

export async function executeIndexedDbRequest<T>(
	mode: IDBTransactionMode,
	createRequest: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
	const database = await getDb();

	return new Promise((resolve, reject) => {
		let settled = false;

		const closeDatabase = () => {
			database.close();
		};

		const rejectOnce = (error: Error) => {
			if (settled) {
				return;
			}

			settled = true;
			closeDatabase();
			reject(error);
		};

		try {
			const transaction = database.transaction(STORE_NAME, mode);
			const store = transaction.objectStore(STORE_NAME);
			const request = createRequest(store);

			request.onsuccess = () => {
				if (settled) {
					return;
				}

				settled = true;
				closeDatabase();
				resolve(request.result);
			};

			request.onerror = () => {
				rejectOnce(request.error ?? new Error('IndexedDB request failed.'));
			};

			transaction.onerror = () => {
				rejectOnce(
					transaction.error ?? new Error('IndexedDB transaction failed.')
				);
			};

			transaction.onabort = () => {
				rejectOnce(
					transaction.error ?? new Error('IndexedDB transaction aborted.')
				);
			};
		} catch (error) {
			rejectOnce(
				error instanceof Error ? error : new Error('IndexedDB request failed.')
			);
		}
	});
}
